import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchImages, uploadImage, importImages, exportImages, updateImage, deleteImage, saveAnnotations, me, logout,
  fetchTags, createTag, updateTag, deleteTag,
  fetchUsers, createUser, updateUser, deleteUser, resetUserPassword,
  fetchUserSessions, terminateAllSessions, terminateSession,
  exportAnnotationsJSON,
  fetchLanguages, createLanguage, deleteLanguage,
  fetchLanguageTranslations, saveLanguageTranslations, fetchTranslationDefaults,
  fetchVersion,
} from './utils/api';
import { useToast } from './hooks/useToast';
import { useTheme } from './hooks/useTheme';
import { useI18n } from './contexts/I18nContext';
import AnnotationEditor from './components/AnnotationEditor';
import ImageViewer from './components/ImageViewer';
import RichTextEditor from './components/RichTextEditor';
import { PropertiesEditor, JsonImport, propsToModel, propsToJSON, mergeRecordEntries } from './components/PropertiesEditor';
import Login from './components/Login';
import './styles/App.css';

const TAG_PALETTE = [
  '#5b5ef4','#e84393','#00d4aa','#ffb830','#ff4d6d',
  '#a78bfa','#34d399','#fb923c','#38bdf8','#f472b6',
];

// Format an RFC 3339 timestamp in the active locale; falls back to the raw
// string (e.g. "unknown" in dev builds) when it isn't a valid date.
function formatDateTime(value, locale) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const { t, lang, setLang, languages } = useI18n();
  const [user, setUser]           = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [images, setImages]       = useState([]);
  const [tags, setTags]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editingImage, setEditingImage] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  // When the editor is opened from the image viewer, remember to return to the
  // viewer on close (rather than falling back to the gallery).
  const [editorReturnToViewer, setEditorReturnToViewer] = useState(null);
  const [showUpload, setShowUpload]     = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState(() => new Set()); // multi-select for bulk actions
  const [bulkBusy, setBulkBusy]         = useState(false);
  const [editingImageMeta, setEditingImageMeta] = useState(null); // rename+tags
  const [search, setSearch]       = useState('');
  const [filterTags, setFilterTags] = useState([]);  // selected tag IDs for filter
  const [showTags, setShowTags]   = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [version, setVersion]     = useState(null);
  const { show: toast, ToastEl } = useToast();
  const [theme, toggleTheme] = useTheme();

  useEffect(() => {
    me().then(setUser).catch(() => setUser(null)).finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    fetchVersion().then(setVersion).catch(() => {});
  }, []);

  const loadTags = useCallback(async () => {
    try { setTags(await fetchTags()); } catch {}
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [imgs] = await Promise.all([fetchImages(), loadTags()]);
      setImages(imgs);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [toast, user, loadTags]);

  useEffect(() => { if (user) load(); }, [load, user]);

  const handleLogout = async () => {
    await logout();
    setUser(null);
    setImages([]);
    setTags([]);
  };

  const handleUpload = async (file, name, tagIds) => {
    try {
      const img = await uploadImage(file, name, tagIds);
      setImages(prev => [img, ...prev]);
      setShowUpload(false);
      toast(t('image.added'));
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleImport = async (file) => {
    const res = await importImages(file);
    await load();
    const parts = [
      `${res.images || 0} ${t('import.images')}`,
      `${res.annotations || 0} ${t('import.annotations')}`,
    ];
    if (res.tags_created && res.tags_created.length) {
      parts.push(`${res.tags_created.length} ${t('import.tags')}`);
    }
    toast(`${t('import.done')}: ${parts.join(', ')}`);
    if (res.errors && res.errors.length) {
      toast(`${res.errors.length} ${t('import.warnings')}`, 'error');
    }
    setShowImport(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('image.confirm_delete'))) return;
    try {
      await deleteImage(id);
      setImages(prev => prev.filter(x => x.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      toast(t('image.deleted'));
    } catch (e) { toast(e.message, 'error'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !window.confirm(t('gallery.confirm_delete_selected').replace('{n}', ids.length))) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map(id => deleteImage(id)));
      setImages(prev => prev.filter(x => !selectedIds.has(x.id)));
      setSelectedIds(new Set());
      toast(t('image.deleted'));
    } catch (e) { toast(e.message, 'error'); }
    finally { setBulkBusy(false); }
  };

  const handleBulkDownload = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const blob = await exportImages(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annotatex-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBulkBusy(false); }
  };

  const handleUpdateImageMeta = async (id, name, description, tagIds, link, properties) => {
    try {
      const updated = await updateImage(id, { name, description, link, properties, tag_ids: tagIds });
      setImages(prev => prev.map(x => x.id === id ? updated : x));
      setEditingImageMeta(null);
      toast(t('image.updated'));
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleSaveAnnotations = async (annotations) => {
    try {
      const updated = await saveAnnotations(editingImage.id, annotations);
      setImages(prev => prev.map(x => x.id === updated.id ? updated : x));
      setEditingImage(updated);
      toast(t('editor.annotations_saved'));
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleSaveEditorTags = async (tagIds) => {
    try {
      const updated = await updateImage(editingImage.id, { name: editingImage.name, tag_ids: tagIds });
      setImages(prev => prev.map(x => x.id === updated.id ? updated : x));
      setEditingImage(updated);
    } catch (e) { toast(e.message, 'error'); }
  };

  const toggleFilterTag = (id) => {
    setFilterTags(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filtered = images.filter(img => {
    const matchSearch = img.name.toLowerCase().includes(search.toLowerCase());
    const matchTags = filterTags.length === 0 ||
      filterTags.some(tid => (img.tags || []).some(t => t.id === tid));
    return matchSearch && matchTags;
  });

  if (authLoading) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) return <Login onLogin={(data) => setUser(data)} />;

  if (editingImage) {
    return (
      <>
        <AnnotationEditor
          image={editingImage}
          annotations={editingImage.annotations}
          onSave={handleSaveAnnotations}
          onClose={() => {
            setEditingImage(null);
            if (editorReturnToViewer) {
              setViewingImage(editorReturnToViewer);
              setEditorReturnToViewer(null);
            }
          }}
          allTags={tags}
          onSaveTags={handleSaveEditorTags}
        />
        {ToastEl}
      </>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <button className="sidebar-toggle-btn" title={t('header.menu_title')} onClick={() => setShowSidebar(s => !s)}>☰</button>
        <div className="header-brand">
          <div className="brand-icon">⬡</div>
          <div>
            <div className="brand-name">AnnotateX</div>
            <div className="brand-sub">{t('header.brand_sub')}</div>
          </div>
        </div>
        <div className="header-center">
          <input
            className="search-input"
            placeholder={t('header.search_placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="header-actions">
          <span className="badge badge-accent">{images.length} {t('gallery.images_count')}</span>
          <button className="btn btn-primary" title={t('upload.title')} onClick={() => setShowUpload(true)}>{t('header.add_image')}</button>
          <button className="btn btn-secondary" title={t('import.title')} onClick={() => setShowImport(true)}>{t('header.import')}</button>
          <button className="btn btn-secondary btn-sm" title={t('header.toggle_theme')} onClick={toggleTheme}>◑</button>
        </div>
      </header>

      <div className="app-body">
        {/* Left sidebar */}
        <div className={`app-sidebar${showSidebar ? ' open' : ''}`}>
          <div className="app-sidebar-top">
            <span className="app-sidebar-heading">Menu</span>
            <button className="btn-icon" title={t('common.close')} onClick={() => setShowSidebar(false)}>✕</button>
          </div>

          {/* User */}
          <div className="app-sidebar-section">
            <div className="app-sidebar-user">
              <span>👤</span>
              <div>
                <div className="app-sidebar-username">{user?.username}</div>
                {user?.is_admin && <span className="badge badge-accent" style={{fontSize:9,padding:'1px 5px',lineHeight:1.4}}>admin</span>}
              </div>
            </div>
            <button className="app-sidebar-btn" onClick={() => setShowProfile(true)}>🔑 {t('profile.title')}</button>
            <button className="app-sidebar-btn" onClick={handleLogout}>⏻ {t('header.logout')}</button>
          </div>

          {/* Language */}
          {languages.length > 1 && (
            <div className="app-sidebar-section">
              <div className="app-sidebar-lang-row">
                <span>🌐</span>
                <select className="lang-toggle" value={lang} onChange={e => setLang(e.target.value)}>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.name || l.code.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* CRUD */}
          <div className="app-sidebar-section">
            <button className="app-sidebar-btn" onClick={() => setShowTags(true)}>🏷 {t('header.manage_tags')}</button>
            {user?.is_admin && <button className="app-sidebar-btn" onClick={() => setShowUsers(true)}>👥 {t('header.manage_users')}</button>}
            {user?.is_admin && <button className="app-sidebar-btn" onClick={() => setShowLanguages(true)}>🌐 {t('header.manage_languages')}</button>}
          </div>

          {/* Documentation */}
          <div className="app-sidebar-section">
            <button className="app-sidebar-btn" onClick={() => {
              const docLocale = lang && lang.startsWith('pt') ? 'pt-BR' : 'en';
              window.open(`/doc/${docLocale}/`, '_blank');
            }}>📖 {t('header.documentation')}</button>
            <button className="app-sidebar-btn" onClick={() => window.open('/api/embed-gallery', '_blank')}>🖼 {t('header.embed_gallery')}</button>
          </div>

          {/* Build version — shows when the running executable was last changed */}
          {version && (
            <div
              className="app-sidebar-version"
              title={[
                `${t('version.branch')}: ${version.branch}`,
                `${t('version.commit')}: ${version.commit}`,
                `${t('version.commit_time')}: ${formatDateTime(version.commit_time, lang)}`,
                `${t('version.executable_time')}: ${formatDateTime(version.executable_time, lang)}`,
              ].join('\n')}
            >
              <span>🕒</span>
              <span className="app-sidebar-version-text">
                {t('version.executable_time')}: {formatDateTime(version.executable_time, lang)}
              </span>
            </div>
          )}
        </div>

        {/* Main content area */}
        <div className="app-content-area">
          {/* Tag filter bar */}
          {tags.length > 0 && (
            <div className="tag-filter-bar">
              <span className="tag-filter-label">{t('gallery.filter_label')}</span>
              {tags.map(tag => (
                <button
                  key={tag.id}
                  className={`tag-pill ${filterTags.includes(tag.id) ? 'active' : ''}`}
                  style={{ '--tag-color': tag.color }}
                  onClick={() => toggleFilterTag(tag.id)}
                >
                  {tag.name}
                </button>
              ))}
              {filterTags.length > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => setFilterTags([])}>{t('gallery.filter_clear')}</button>
              )}
            </div>
          )}

          <main className="app-main">
            {selectedIds.size > 0 && (
              <div className="selection-bar">
                <span className="selection-bar-count">
                  {t('gallery.selected_count').replace('{n}', selectedIds.size)}
                </span>
                <div className="selection-bar-actions">
                  <button className="btn btn-secondary btn-sm" disabled={bulkBusy}
                    onClick={() => setSelectedIds(new Set(filtered.map(i => i.id)))}>
                    {t('gallery.select_all')}
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={bulkBusy} onClick={handleBulkDownload}>
                    ⤓ {t('gallery.download_targz')}
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={bulkBusy} onClick={handleBulkDelete}>
                    ✕ {t('gallery.delete_selected')}
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}>
                    {t('gallery.clear_selection')}
                  </button>
                </div>
              </div>
            )}
            {loading ? (
              <div className="app-loading"><div className="loading-spinner" /><p>{t('gallery.loading')}</p></div>
            ) : filtered.length === 0 ? (
              <div className="app-empty">
                <div className="empty-icon">◎</div>
                <h2>{search || filterTags.length ? t('gallery.empty') : t('gallery.empty_start')}</h2>
                <p>{search || filterTags.length ? t('gallery.empty_hint') : t('gallery.empty_start_hint')}</p>
                {!search && !filterTags.length && (
                  <button className="btn btn-primary" onClick={() => setShowUpload(true)}>{t('header.add_image')}</button>
                )}
              </div>
            ) : (
              <div className="images-grid">
                {filtered.map(img => (
                  <ImageCard
                    key={img.id}
                    image={img}
                    selected={selectedIds.has(img.id)}
                    onToggleSelect={() => toggleSelect(img.id)}
                    onAnnotate={() => setEditingImage(img)}
                    onView={() => setViewingImage(img)}
                    onDelete={() => handleDelete(img.id)}
                    onEdit={() => setEditingImageMeta(img)}
                    onExport={() => exportAnnotationsJSON(img)}
                    onExportSVG={() => window.open(`/public/images/${img.id}.svg`, '_blank')}
                    onCopySvgUrl={() => copyToClipboard(`${window.location.origin}/public/images/${img.id}.svg`)
                      .then(() => toast(t('image.svg_url_copied')))
                      .catch(() => toast(t('image.copy_failed'), 'error'))}
                    onCopyThumbUrl={() => copyToClipboard(`${window.location.origin}/uploads/${img.thumbnail || img.filename}`)
                      .then(() => toast(t('image.thumb_url_copied')))
                      .catch(() => toast(t('image.copy_failed'), 'error'))}
                    onEmbedExample={() => window.open(`/api/images/${img.id}/embed-example`, '_blank')}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {showUpload && (
        <UploadModal tags={tags} onUpload={handleUpload} onClose={() => setShowUpload(false)} />
      )}

      {showImport && (
        <ImportModal onImport={handleImport} onClose={() => setShowImport(false)} />
      )}

      {editingImageMeta && (
        <ImageMetaModal
          image={editingImageMeta}
          tags={tags}
          onSave={handleUpdateImageMeta}
          onClose={() => setEditingImageMeta(null)}
        />
      )}

      {viewingImage && (
        <ImageViewer
          image={images.find(x => x.id === viewingImage.id) || viewingImage}
          onClose={() => setViewingImage(null)}
          onEdit={() => {
            setEditingImage(images.find(x => x.id === viewingImage.id) || viewingImage);
            setEditorReturnToViewer(viewingImage);
            setViewingImage(null);
          }}
        />
      )}

      {showTags && (
        <TagsModal
          tags={tags}
          onClose={() => setShowTags(false)}
          onChange={loadTags}
          toast={toast}
        />
      )}

      {showUsers && user?.is_admin && (
        <UsersModal onClose={() => setShowUsers(false)} toast={toast} currentUserId={user.id} />
      )}

      {showProfile && (
        <ProfileModal
          user={user}
          onClose={() => setShowProfile(false)}
          toast={toast}
          onPasswordChanged={() => {}}
        />
      )}

      {showLanguages && user?.is_admin && (
        <LanguagesModal onClose={() => setShowLanguages(false)} toast={toast} />
      )}

      {ToastEl}
    </div>
  );
}

// ── ImageCard ─────────────────────────────────────────────────────────────────

// copyToClipboard copies text using the async Clipboard API, falling back to a
// hidden textarea + execCommand for non-secure contexts (plain http on a remote
// host, where navigator.clipboard is unavailable). Returns a promise.
async function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    if (!document.execCommand('copy')) throw new Error('copy command failed');
  } finally {
    document.body.removeChild(ta);
  }
}

function ImageCard({ image, selected, onToggleSelect, onAnnotate, onView, onDelete, onEdit, onExport, onExportSVG, onCopySvgUrl, onCopyThumbUrl, onEmbedExample }) {
  const { t } = useI18n();
  const annCount = image.annotations?.length || 0;
  const imgTags  = image.tags || [];

  return (
    <div className={`image-card${selected ? ' selected' : ''}`}>
      <label className="image-card-select" title={t('gallery.select_image')} onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={!!selected} onChange={onToggleSelect} />
      </label>
      <div className="image-card-thumb" onClick={onView}>
        <img
          src={`/uploads/${image.thumbnail || image.filename}`}
          alt={image.name}
          onError={image.thumbnail ? (e) => { e.target.onerror = null; e.target.src = `/uploads/${image.filename}`; } : undefined}
        />
        <div className="image-card-overlay">
          <button className="btn btn-secondary btn-sm">{t('image.view')}</button>
        </div>
        {annCount > 0 && (
          <div className="ann-badge-float"><span>{annCount} ✦</span></div>
        )}
      </div>
      <div className="image-card-info">
        <div className="image-card-name" title={image.name}>{image.name}</div>
        <div className="image-card-meta">
          <span>{image.original_width}×{image.original_height}</span>
          <span>{annCount} {t('image.annotations_count')}</span>
        </div>
        {imgTags.length > 0 && (
          <div className="image-card-tags">
            {imgTags.map(tag => (
              <span key={tag.id} className="tag-pill tag-pill-sm" style={{ '--tag-color': tag.color }}>{tag.name}</span>
            ))}
          </div>
        )}
        <div className="image-card-actions">
          <button className="btn btn-primary btn-sm" title={t('image.annotate')} onClick={onAnnotate}>{t('image.annotate')}</button>
          <button className="btn btn-secondary btn-sm" title={t('image.view')} onClick={onView}>{t('image.view')}</button>
          {image.link && (
            <a className="btn btn-secondary btn-sm" href={image.link} target="_blank" rel="noopener noreferrer"
              title={t('image.open_link')} onClick={e => e.stopPropagation()}>🔗</a>
          )}
          <button className="btn btn-secondary btn-sm" title={t('image.edit')} onClick={onEdit}>✏</button>
          <button className="btn btn-secondary btn-sm" onClick={onExport} title={t('image.export_json')}>⬇ JSON</button>
          <button className="btn btn-secondary btn-sm" onClick={onExportSVG} title={t('image.export_svg')}>⬇ SVG</button>
          <button className="btn btn-secondary btn-sm" onClick={onCopySvgUrl} title={t('image.copy_svg_url')}>⧉ SVG</button>
          <button className="btn btn-secondary btn-sm" onClick={onCopyThumbUrl} title={t('image.copy_thumb_url')}>⧉ IMG</button>
          <button className="btn btn-secondary btn-sm" onClick={onEmbedExample} title={t('image.embed_example')}>⟨/⟩</button>
          <button className="btn btn-danger btn-sm" title={t('image.delete')} onClick={onDelete}>✕</button>
        </div>
      </div>
    </div>
  );
}

// ── UploadModal ───────────────────────────────────────────────────────────────

function UploadModal({ tags, onUpload, onClose }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [mode, setMode]       = useState('file'); // 'file' | 'url'
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [url, setUrl]         = useState('');
  const [urlPreview, setUrlPreview] = useState('');
  const [name, setName]       = useState('');
  const [loading, setLoading] = useState(false);
  const [selTags, setSelTags] = useState([]);

  const handleFile = (f) => {
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
    setPreview(URL.createObjectURL(f));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f?.type.startsWith('image/')) handleFile(f);
  };

  const handleUrlChange = (e) => {
    const u = e.target.value;
    setUrl(u);
    setUrlPreview(u);
    if (!name && u) {
      const last = u.split('/').pop().split('?')[0];
      if (last) setName(last.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (mode === 'file') {
        await onUpload(file, name, selTags);
      } else {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('Failed to fetch image from URL');
        const blob = await resp.blob();
        const ext = url.split('?')[0].split('.').pop().toLowerCase();
        const fname = name + (['jpg','jpeg','png','gif','webp','avif'].includes(ext) ? '.' + ext : '.jpg');
        await onUpload(new File([blob], fname, { type: blob.type }), name, selTags);
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = mode === 'file' ? (file && name) : (url && name);

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{t('upload.title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="upload-mode-tabs">
            <button className={`upload-mode-tab ${mode === 'file' ? 'active' : ''}`}
              onClick={() => setMode('file')}>{t('upload.tab_file')}</button>
            <button className={`upload-mode-tab ${mode === 'url' ? 'active' : ''}`}
              onClick={() => setMode('url')}>{t('upload.tab_url')}</button>
          </div>
          {mode === 'file' ? (
            <div
              className={`drop-zone ${file ? 'has-file' : ''}`}
              onDrop={handleDrop} onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('file-input').click()}
            >
              {preview
                ? <img src={preview} alt="preview" className="drop-preview" />
                : (<><div className="drop-icon">⬆</div><div className="drop-text">{t('upload.drop_text')}</div><div className="drop-hint">{t('upload.drop_hint')}</div></>)
              }
              <input id="file-input" type="file" accept="image/*" hidden
                onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
            </div>
          ) : (
            <div className="url-input-zone">
              <input
                type="url"
                placeholder={t('upload.url_placeholder')}
                value={url}
                onChange={handleUrlChange}
                className="url-input"
              />
              {urlPreview && (
                <div className="drop-zone has-file" style={{ cursor: 'default', pointerEvents: 'none' }}>
                  <img src={urlPreview} alt="preview" className="drop-preview"
                    onError={() => setUrlPreview('')} />
                </div>
              )}
            </div>
          )}
          <div className="field">
            <label>{t('upload.name_label')}</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder={t('upload.name_placeholder')} />
          </div>
          {tags.length > 0 && (
            <div className="field">
              <label>{t('upload.tags_label')}</label>
              <div className="tag-picker">
                {tags.map(tag => (
                  <button key={tag.id} type="button"
                    className={`tag-pill ${selTags.includes(tag.id) ? 'active' : ''}`}
                    style={{ '--tag-color': tag.color }}
                    onClick={() => setSelTags(p => p.includes(tag.id) ? p.filter(x => x !== tag.id) : [...p, tag.id])}>
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!canSubmit || loading} onClick={handleSubmit}>
            {loading ? t('upload.submitting') : t('upload.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ImportModal ─────────────────────────────────────────────────────────────────

function ImportModal({ onImport, onClose }) {
  const { t } = useI18n();
  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);

  const accept = '.zip,.tar.gz,.tgz,application/zip,application/gzip,application/x-gzip';
  const isArchive = (f) => f && /\.(zip|tgz|tar\.gz)$/i.test(f.name);

  const handleFile = (f) => { if (isArchive(f)) setFile(f); };
  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      await onImport(file);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{t('import.title')}</h2>
          <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 0 }}>{t('import.help')}</p>
          <div
            className={`drop-zone ${file ? 'has-file' : ''}`}
            onDrop={handleDrop} onDragOver={e => e.preventDefault()}
            onClick={() => document.getElementById('import-file-input').click()}
          >
            {file
              ? (<><div className="drop-icon">🗜</div><div className="drop-text">{file.name}</div></>)
              : (<><div className="drop-icon">⬆</div><div className="drop-text">{t('import.drop_text')}</div><div className="drop-hint">{t('import.drop_hint')}</div></>)
            }
            <input id="import-file-input" type="file" accept={accept} hidden
              onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!file || loading} onClick={handleSubmit}>
            {loading ? t('import.submitting') : t('import.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ImageMetaModal ────────────────────────────────────────────────────────────

function ImageMetaModal({ image, tags, onSave, onClose }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [name, setName]       = useState(image.name);
  const [description, setDescription] = useState(image.description || '');
  const [link, setLink]       = useState(image.link || '');
  const [selTags, setSelTags] = useState((image.tags || []).map(tag => tag.id));
  const [propsModel, setPropsModel] = useState(() => propsToModel(image.properties));

  const save = () => onSave(image.id, name, description, selTags, link, propsToJSON(propsModel));

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 620 }}>
        <div className="modal-header">
          <h2>{t('image_meta.title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>{t('image_meta.name_label')}</label>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              onKeyDown={e => e.key === 'Enter' && name && save()} />
          </div>
          <div className="field">
            <label>{t('image_meta.link_label')}</label>
            <input type="url" value={link} onChange={e => setLink(e.target.value)}
              placeholder={t('image_meta.link_placeholder')} />
          </div>
          <div className="field">
            <label>{t('image_meta.description_label')}</label>
            <RichTextEditor value={description} onChange={setDescription} placeholder={t('image_meta.description_placeholder')} />
          </div>
          <div className="field">
            <label>{t('image_meta.properties_label')}</label>
            <PropertiesEditor entries={propsModel} onChange={setPropsModel} />
            <JsonImport onImport={p => setPropsModel(m => mergeRecordEntries(m, p))} />
          </div>
          {tags.length > 0 && (
            <div className="field">
              <label>{t('upload.tags_label')}</label>
              <div className="tag-picker">
                {tags.map(tag => (
                  <button key={tag.id} type="button"
                    className={`tag-pill ${selTags.includes(tag.id) ? 'active' : ''}`}
                    style={{ '--tag-color': tag.color }}
                    onClick={() => setSelTags(p => p.includes(tag.id) ? p.filter(x => x !== tag.id) : [...p, tag.id])}>
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!name} onClick={save}>{t('common.save')}</button>
        </div>
      </div>
    </div>
  );
}

// ── TagsModal ─────────────────────────────────────────────────────────────────

function TagsModal({ tags, onClose, onChange, toast }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [editingTag, setEditingTag] = useState(null); // null | {id?,name,color}
  const [saving, setSaving]         = useState(false);

  const handleSave = async () => {
    if (!editingTag?.name) return;
    setSaving(true);
    try {
      if (editingTag.id) {
        await updateTag(editingTag.id, { name: editingTag.name, color: editingTag.color });
        toast(t('tags.updated'));
      } else {
        await createTag(editingTag.name, editingTag.color);
        toast(t('tags.created'));
      }
      setEditingTag(null);
      onChange();
    } catch (e) { toast(e.message, 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('tags.confirm_delete'))) return;
    try {
      await deleteTag(id);
      toast(t('tags.deleted'));
      onChange();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{t('tags.title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          {editingTag ? (
            <div>
              <div className="field">
                <label>{editingTag.id ? t('tags.edit_tag') : t('tags.new_tag_label')}</label>
                <input autoFocus value={editingTag.name}
                  onChange={e => setEditingTag(tag => ({ ...tag, name: e.target.value }))}
                  placeholder={t('tags.name_placeholder')}
                  onKeyDown={e => e.key === 'Enter' && handleSave()} />
              </div>
              <div className="field">
                <label>{t('tags.color_label')}</label>
                <div className="tag-color-picker">
                  {TAG_PALETTE.map(c => (
                    <button key={c} type="button"
                      className={`color-swatch-lg ${editingTag.color === c ? 'active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setEditingTag(tag => ({ ...tag, color: c }))} />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setEditingTag(null)}>{t('common.cancel')}</button>
                <button className="btn btn-primary" disabled={!editingTag.name || saving} onClick={handleSave}>
                  {saving ? t('tags.saving') : t('common.save')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
                onClick={() => setEditingTag({ name: '', color: TAG_PALETTE[0] })}>
                {t('tags.new_tag')}
              </button>
              {tags.length === 0 && (
                <div className="empty-ann" style={{ padding: '20px 0' }}>{t('tags.empty')}</div>
              )}
              <div className="tags-list">
                {tags.map(tag => (
                  <div key={tag.id} className="tag-list-row">
                    <span className="tag-pill" style={{ '--tag-color': tag.color }}>{tag.name}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" title={t('common.edit')}
                        onClick={() => setEditingTag({ id: tag.id, name: tag.name, color: tag.color })}>✎</button>
                      <button className="btn btn-danger btn-sm" title={t('common.delete')} onClick={() => handleDelete(tag.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── UsersModal ────────────────────────────────────────────────────────────────

function UsersModal({ onClose, toast, currentUserId }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [newUserForm, setNewUserForm] = useState(null);
  const [sessionsOf, setSessionsOf]   = useState(null); // {user, sessions}

  const load = useCallback(async () => {
    try { setUsers(await fetchUsers()); }
    catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newUserForm?.username) return;
    try {
      const res = await createUser(newUserForm.username, !!newUserForm.is_admin);
      toast(`${t('users.created')} ${res.password}`, 'success');
      setNewUserForm(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('users.confirm_delete'))) return;
    try {
      await deleteUser(id);
      toast(t('users.deleted'));
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleResetPwd = async (id, username) => {
    if (!window.confirm(`${t('users.confirm_reset_pwd')} "${username}"?`)) return;
    try {
      const res = await resetUserPassword(id);
      toast(`${t('users.pwd_reset')} "${username}": ${res.password}`, 'success');
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleViewSessions = async (u) => {
    try {
      const sessions = await fetchUserSessions(u.id);
      setSessionsOf({ user: u, sessions });
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleTerminateAll = async (userId) => {
    try {
      await terminateAllSessions(userId);
      toast(t('users.terminated'));
      setSessionsOf(null);
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleTerminateOne = async (userId, sid) => {
    try {
      await terminateSession(userId, sid);
      const sessions = await fetchUserSessions(userId);
      setSessionsOf(prev => ({ ...prev, sessions }));
    } catch (e) { toast(e.message, 'error'); }
  };

  if (sessionsOf) {
    return (
      <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
        <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 520 }}>
          <div className="modal-header">
            <h2>{t('users.sessions_title_of')} {sessionsOf.user.username}</h2>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
              <button className="btn-icon" title={t('common.close')} onClick={() => setSessionsOf(null)}>✕</button>
            </div>
          </div>
          <div className="modal-body">
            {sessionsOf.sessions.length === 0
              ? <p style={{ color: 'var(--text2)' }}>{t('users.no_sessions')}</p>
              : sessionsOf.sessions.map(s => (
                <div key={s.session_id} className="session-row">
                  <div>
                    <div className="session-date">{t('users.created_at')} {s.created_at.slice(0, 16).replace('T', ' ')}</div>
                    <div className="session-date">{t('users.expires_at')} {s.expires_at.slice(0, 16).replace('T', ' ')}</div>
                    {s.is_current && <span className="badge badge-success" style={{ fontSize: 11 }}>{t('users.current_session')}</span>}
                  </div>
                  {!s.is_current && (
                    <button className="btn btn-danger btn-sm" title={t('users.terminate')}
                      onClick={() => handleTerminateOne(sessionsOf.user.id, s.session_id)}>{t('users.terminate')}</button>
                  )}
                </div>
              ))
            }
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setSessionsOf(null)}>{t('users.back')}</button>
            <button className="btn btn-danger" title={t('users.terminate_all')} onClick={() => handleTerminateAll(sessionsOf.user.id)}>{t('users.terminate_all')}</button>
          </div>
        </div>
      </div>
    );
  }

  if (editingUser) {
    return (
      <EditUserModal
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={() => { setEditingUser(null); load(); toast(t('users.updated')); }}
        toast={toast}
      />
    );
  }

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{t('users.title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          {newUserForm ? (
            <div className="new-user-form">
              <div className="field">
                <label>{t('users.username_label')}</label>
                <input autoFocus value={newUserForm.username}
                  onChange={e => setNewUserForm(f => ({ ...f, username: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()} />
              </div>
              <label className="checkbox-label">
                <input type="checkbox" checked={!!newUserForm.is_admin}
                  onChange={e => setNewUserForm(f => ({ ...f, is_admin: e.target.checked }))} />
                {t('users.admin_label')}
              </label>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-secondary" onClick={() => setNewUserForm(null)}>{t('common.cancel')}</button>
                <button className="btn btn-primary" disabled={!newUserForm.username} onClick={handleCreate}>{t('users.create')}</button>
              </div>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
              onClick={() => setNewUserForm({ username: '', is_admin: false })}>
              {t('users.new_user')}
            </button>
          )}

          {loading ? <div className="loading-spinner" /> : (
            <div className="users-list">
              {users.map(u => (
                <div key={u.id} className="user-row">
                  <div className="user-info">
                    <span className="user-name">{u.username}</span>
                    {u.is_admin && <span className="badge badge-accent" style={{ fontSize: 11 }}>admin</span>}
                    {u.id === currentUserId && <span className="badge badge-success" style={{ fontSize: 11 }}>{t('users.you_badge')}</span>}
                  </div>
                  <div className="user-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingUser(u)} title={t('users.edit_title')}>✎</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleResetPwd(u.id, u.username)} title={t('users.reset_pwd_title')}>🔑</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleViewSessions(u)} title={t('users.sessions_title')}>📋</button>
                    {u.id !== currentUserId && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u.id)} title={t('users.delete_title')}>✕</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, toast }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [username, setUsername] = useState(user.username);
  const [isAdmin, setIsAdmin]   = useState(user.is_admin);
  const [saving, setSaving]     = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUser(user.id, { username, is_admin: isAdmin });
      onSaved();
    } catch (e) { toast(e.message, 'error'); }
    setSaving(false);
  };

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 400 }}>
        <div className="modal-header">
          <h2>{t('users.edit_title')} {user.username}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>{t('users.username_label')}</label>
            <input value={username} onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()} />
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            {t('users.admin_label')}
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!username || saving} onClick={handleSave}>
            {saving ? t('users.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ProfileModal ──────────────────────────────────────────────────────────────

function ProfileModal({ user, onClose, toast }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [tab, setTab]             = useState('password'); // 'password' | 'sessions'
  const [oldPwd, setOldPwd]       = useState('');
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [sessions, setSessions]   = useState([]);
  const [saving, setSaving]       = useState(false);

  const loadSessions = useCallback(async () => {
    try { setSessions(await fetchUserSessions(user.id)); }
    catch (e) { toast(e.message, 'error'); }
  }, [user.id, toast]);

  useEffect(() => {
    if (tab === 'sessions') loadSessions();
  }, [tab, loadSessions]);

  const handleChangePassword = async () => {
    if (newPwd !== confirmPwd) { toast(t('profile.pwd_mismatch'), 'error'); return; }
    if (newPwd.length < 6) { toast(t('profile.pwd_too_short'), 'error'); return; }
    setSaving(true);
    try {
      await updateUser(user.id, { old_password: oldPwd, new_password: newPwd });
      toast(t('profile.pwd_changed'));
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) { toast(e.message, 'error'); }
    setSaving(false);
  };

  const handleTerminateAll = async () => {
    try {
      await terminateAllSessions(user.id);
      toast(t('profile.terminated'));
      loadSessions();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleTerminateOne = async (sid) => {
    try {
      await terminateSession(user.id, sid);
      loadSessions();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 460 }}>
        <div className="modal-header">
          <h2>👤 {user.username}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="profile-tabs">
            <button className={`profile-tab ${tab === 'password' ? 'active' : ''}`} onClick={() => setTab('password')}>{t('profile.tab_password')}</button>
            <button className={`profile-tab ${tab === 'sessions' ? 'active' : ''}`} onClick={() => setTab('sessions')}>{t('profile.tab_sessions')}</button>
          </div>

          {tab === 'password' && (
            <div>
              <div className="field">
                <label>{t('profile.current_password')}</label>
                <input type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} autoComplete="current-password" />
              </div>
              <div className="field">
                <label>{t('profile.new_password')}</label>
                <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} autoComplete="new-password" />
              </div>
              <div className="field">
                <label>{t('profile.confirm_password')}</label>
                <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleChangePassword()} />
              </div>
              <button className="btn btn-primary" disabled={!oldPwd || !newPwd || saving} onClick={handleChangePassword}>
                {saving ? t('profile.changing') : t('profile.change_btn')}
              </button>
            </div>
          )}

          {tab === 'sessions' && (
            <div>
              {sessions.length === 0
                ? <p style={{ color: 'var(--text2)' }}>{t('profile.no_sessions')}</p>
                : sessions.map(s => (
                  <div key={s.session_id} className="session-row">
                    <div>
                      <div className="session-date">{t('users.created_at')} {s.created_at.slice(0, 16).replace('T', ' ')}</div>
                      <div className="session-date">{t('users.expires_at')} {s.expires_at.slice(0, 16).replace('T', ' ')}</div>
                      {s.is_current && <span className="badge badge-success" style={{ fontSize: 11 }}>{t('users.current_session')}</span>}
                    </div>
                    {!s.is_current && (
                      <button className="btn btn-danger btn-sm" title={t('users.terminate')} onClick={() => handleTerminateOne(s.session_id)}>{t('users.terminate')}</button>
                    )}
                  </div>
                ))
              }
              {sessions.filter(s => !s.is_current).length > 0 && (
                <button className="btn btn-danger btn-sm" style={{ marginTop: 8 }} title={t('profile.terminate_others')} onClick={handleTerminateAll}>
                  {t('profile.terminate_others')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── LanguagesModal ────────────────────────────────────────────────────────────

function LanguagesModal({ onClose, toast }) {
  const { t, setLanguages: setCtxLanguages } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [langs, setLangs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [editingLang, setEditingLang] = useState(null); // Language being edited in TranslationsEditor

  const load = useCallback(async () => {
    try {
      const data = await fetchLanguages();
      setLangs(data);
      setCtxLanguages(data);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [toast, setCtxLanguages]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newCode || !newName) return;
    try {
      await createLanguage(newCode.trim(), newName.trim());
      toast(t('languages.created'));
      setNewCode('');
      setNewName('');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('languages.confirm_delete'))) return;
    try {
      await deleteLanguage(id);
      toast(t('languages.deleted'));
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  if (editingLang) {
    return (
      <TranslationsEditor
        lang={editingLang}
        onBack={() => setEditingLang(null)}
        onClose={onClose}
        toast={toast}
      />
    );
  }

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{t('languages.title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          {/* Create new language */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              value={newCode}
              onChange={e => setNewCode(e.target.value)}
              placeholder={t('languages.code_placeholder')}
              style={{ width: 80 }}
            />
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder={t('languages.name_placeholder')}
              style={{ flex: 1 }}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <button className="btn btn-primary btn-sm" disabled={!newCode || !newName} onClick={handleCreate}>
              {t('languages.create')}
            </button>
          </div>

          {loading ? <div className="loading-spinner" /> : (
            <div className="users-list">
              {langs.map(lang => (
                <div key={lang.id} className="user-row">
                  <div className="user-info">
                    <span className="user-name">{lang.name}</span>
                    <span className="badge badge-accent" style={{ fontSize: 11 }}>{lang.code}</span>
                    {lang.is_system && (
                      <span className="badge badge-success" style={{ fontSize: 11 }}>{t('languages.system_badge')}</span>
                    )}
                  </div>
                  <div className="user-actions">
                    <button className="btn btn-secondary btn-sm" title={t('languages.messages_btn')}
                      onClick={() => setEditingLang(lang)}>
                      {t('languages.messages_btn')}
                    </button>
                    {!lang.is_system && (
                      <button className="btn btn-danger btn-sm" title={t('common.delete')}
                        onClick={() => handleDelete(lang.id)}>✕</button>
                    )}
                  </div>
                </div>
              ))}
              {langs.filter(l => !l.is_system).length === 0 && langs.length > 0 && (
                <div className="empty-ann" style={{ padding: '12px 0' }}>{t('languages.empty')}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── TranslationsEditor ────────────────────────────────────────────────────────

function TranslationsEditor({ lang, onBack, onClose, toast }) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [tab, setTab]           = useState('frontend'); // 'frontend' | 'backend'
  const [translations, setTranslations] = useState({ frontend: {}, backend: {} });
  const [defaults, setDefaults] = useState({ frontend: {}, backend: {} });
  const [filter, setFilter]     = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [trans, defs] = await Promise.all([
          fetchLanguageTranslations(lang.id),
          fetchTranslationDefaults(),
        ]);
        setTranslations(trans);
        setDefaults(defs);
      } catch (e) { toast(e.message, 'error'); }
    };
    load();
  }, [lang.id, toast]);

  const handleChange = (scope, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [scope]: { ...prev[scope], [key]: value },
    }));
  };

  const handleReset = (scope, key) => {
    const def = defaults[scope]?.[key] ?? '';
    handleChange(scope, key, def);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveLanguageTranslations(lang.id, translations.frontend, translations.backend);
      toast(t('languages.messages_saved'));
    } catch (e) { toast(e.message, 'error'); }
    setSaving(false);
  };

  const currentScope = translations[tab] || {};
  const currentDefaults = defaults[tab] || {};
  const allKeys = Object.keys(currentDefaults);
  const filteredKeys = filter
    ? allKeys.filter(k => k.toLowerCase().includes(filter.toLowerCase()) ||
        (currentScope[k] || '').toLowerCase().includes(filter.toLowerCase()))
    : allKeys;

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 700 }}>
        <div className="modal-header">
          <h2>{t('languages.edit_title')} {lang.name} ({lang.code})</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="profile-tabs" style={{ marginBottom: 12 }}>
            <button className={`profile-tab ${tab === 'frontend' ? 'active' : ''}`} onClick={() => setTab('frontend')}>
              {t('languages.tab_frontend')}
            </button>
            <button className={`profile-tab ${tab === 'backend' ? 'active' : ''}`} onClick={() => setTab('backend')}>
              {t('languages.tab_backend')}
            </button>
          </div>
          <div className="translations-filter">
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder={t('languages.filter_placeholder')}
              style={{ width: '100%' }}
            />
          </div>
          <div className="translations-scroll">
            <table className="translations-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>{t('common.key')}</th>
                  <th>{t('common.value')}</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredKeys.map(key => (
                  <tr key={key}>
                    <td><span className="translation-key">{key}</span></td>
                    <td>
                      <input
                        className="translation-value-input"
                        value={currentScope[key] ?? currentDefaults[key] ?? ''}
                        onChange={e => handleChange(tab, key, e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        title={t('languages.reset_title')}
                        onClick={() => handleReset(tab, key)}
                        style={{ fontSize: 14 }}
                      >↺</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onBack}>{t('users.back')}</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? t('common.loading') : t('languages.save_messages')}
          </button>
        </div>
      </div>
    </div>
  );
}
